import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import useAutoPiP from "../hooks/useAutoPiP";

/**
 * UniversalVideo — robust HTML5 video element that plays:
 *
 *   • Plain MP4 / WebM URLs                       → native <video> direct
 *   • Mux CDN URLs (stream.mux.com/.../720p.mp4)  → native, with HLS fallback
 *     to /<playback-id>.m3u8 when the MP4 codec is rejected by the browser
 *   • Any HLS .m3u8 URL                           → hls.js (Chrome/Edge/FF)
 *                                                   or native Safari
 *
 * Why: real browsers vary in which H.264 profiles they decode natively.
 * Mux serves H.264 High@3.2 MP4 which fails on a small subset of mobile
 * devices but their HLS variant uses widely-compatible baseline chunks.
 *
 * MOBILE PIP: When this video is actively playing and the user backgrounds
 * the browser (switches apps, locks the phone), the embedded `useAutoPiP`
 * hook automatically pops the player into a system-level Picture-in-Picture
 * window so playback continues visibly on top of whatever app the user
 * switches to. Returns to inline placement when they come back.
 *
 * Props:
 *   src        – the primary video URL (MP4 or .m3u8)
 *   poster     – optional poster image
 *   onError    – optional callback when ALL playback paths have failed
 *   autoPiP    – default true; pass false to disable auto-pop-out
 */
const UniversalVideo = React.forwardRef(function UniversalVideo(
  { src, poster, onError, autoPiP = true, captionsUrl = null, captionsLabel = "English", ...rest },
  outerRef
) {
  const innerRef = useRef(null);
  // Expose the inner <video> element to parent callers (CastButton,
  // PiP toggles, etc.) without losing our own internal access.
  useImperativeHandle(outerRef, () => innerRef.current, []);
  const hlsRef = useRef(null);
  const [stage, setStage] = useState("primary"); // primary | hls | failed

  // Auto-PiP on tab-hide. Only "active" while we actually have a src
  // mounted — keeps idle <video> elements from being grabbed.
  useAutoPiP({ videoRef: innerRef, active: !!src, enableAuto: autoPiP });

  // Derive a HLS .m3u8 URL from a Mux MP4 URL, e.g.
  //   https://stream.mux.com/<id>/720p.mp4  →  https://stream.mux.com/<id>.m3u8
  const hlsFallback = (() => {
    if (!src) return null;
    const m = src.match(/^https:\/\/stream\.mux\.com\/([^/]+)\/\d+p\.mp4/);
    return m ? `https://stream.mux.com/${m[1]}.m3u8` : null;
  })();

  // Attach src whenever stage changes.
  useEffect(() => {
    const v = innerRef.current;
    if (!v || !src) return;
    // Tear down any prior HLS instance.
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (_) { /* noop */ }
      hlsRef.current = null;
    }
    let cancelled = false;
    const url = stage === "hls" && hlsFallback ? hlsFallback : src;
    if (url.endsWith(".m3u8")) {
      if (v.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari plays HLS natively.
        v.src = url;
      } else {
        // Code-split: hls.js (~large) is only fetched the first time an
        // .m3u8 stream actually needs it, keeping it out of the initial bundle.
        import("hls.js").then(({ default: Hls }) => {
          if (cancelled || !innerRef.current) return;
          if (Hls.isSupported()) {
            const hls = new Hls({ enableWorker: true });
            hlsRef.current = hls;
            hls.loadSource(url);
            hls.attachMedia(v);
            hls.on(Hls.Events.ERROR, (_, data) => {
              if (data.fatal) {
                setStage("failed");
                if (onError) onError("hls-fatal");
              }
            });
          } else {
            setStage("failed");
            if (onError) onError("hls-unsupported");
          }
        }).catch(() => {
          if (cancelled) return;
          setStage("failed");
          if (onError) onError("hls-unsupported");
        });
      }
    } else {
      v.src = url;
    }
    return () => {
      cancelled = true;
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) { /* noop */ }
        hlsRef.current = null;
      }
    };
  }, [src, stage, hlsFallback, onError]);

  return (
    <video
      ref={innerRef}
      poster={poster || undefined}
      controls
      playsInline
      preload="metadata"
      x-webkit-airplay="allow"
      onError={() => {
        // If the direct MP4 errored AND we have a HLS variant, switch to it.
        if (stage === "primary" && hlsFallback) {
          setStage("hls");
        } else {
          setStage("failed");
          if (onError) onError("native-error");
        }
      }}
      {...rest}
    >
      {captionsUrl && (
        <track
          kind="captions"
          srcLang="en"
          label={captionsLabel}
          src={captionsUrl}
          default
        />
      )}
    </video>
  );
});

export default UniversalVideo;
